import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Form, InputNumber, Layout, Select, Space, Spin, Switch, Typography, Upload, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import settingsService from '../services/settingsService';
import { canEditSettings, isEmployee, isManager } from '../utils/permissions';

function Settings() {
  const [settingsForm] = Form.useForm();
  const { user } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [settingsData, setSettingsData] = useState(null);
  const [fiscalYears, setFiscalYears] = useState([]);
  const [payrollTemplates, setPayrollTemplates] = useState([]);

  const organizationId = user?.organizationId;
  const readOnly = useMemo(() => !canEditSettings(user), [user]);

  useEffect(() => {
    const loadSettings = async () => {
      if (!organizationId) {
        return;
      }

      setLoading(true);
      try {
        const [settingsResponse, fiscalYearsResponse, payrollTemplatesResponse] = await Promise.all([
          settingsService.getOrganizationSettings(organizationId),
          settingsService.getFiscalYears(organizationId),
          settingsService.getPayrollTemplates()
        ]);

        const settings = settingsResponse.data || {};
        const fiscalYearsList = settings.fiscalYears || fiscalYearsResponse.data || [];
        const payrollTemplatesList = settings.payrollTemplates || payrollTemplatesResponse.data || [];

        setSettingsData(settings);
        setFiscalYears(fiscalYearsList);
        setPayrollTemplates(payrollTemplatesList);
        settingsForm.setFieldsValue({
          selectedFiscalYearId: settings.selectedFiscalYearId || undefined,
          selectedPayrollTemplateId: settings.selectedPayrollTemplateId || undefined,
          leaveCarryForwardLimit: settings.leaveCarryForwardLimit ?? 0,
          leaveEncashmentAtFyEnabled: settings.leaveEncashmentAtFyEnabled || false,
          leaveEncashmentAtFyDays: settings.leaveEncashmentAtFyDays ?? 0,
          leaveEncashmentOnExitEnabled: settings.leaveEncashmentOnExitEnabled || false,
          leaveEncashmentOnExitMaxDays: settings.leaveEncashmentOnExitMaxDays ?? 0
        });
      } catch (error) {
        message.error(error.response?.data?.message || 'Failed to load organization settings');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [organizationId, settingsForm, user]);

  const handleLogoUpload = async ({ file, onSuccess, onError }) => {
    if (!organizationId) {
      message.error('Organization is not available');
      onError?.(new Error('Organization is not available'));
      return;
    }

    try {
      setLoading(true);
      const response = await settingsService.uploadOrganizationLogo(organizationId, file);
      setSettingsData((prev) => ({
        ...(prev || {}),
        logo: response.data?.logo || null
      }));
      message.success('Organization logo uploaded successfully');
      onSuccess?.(response, file);
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to upload organization logo');
      onError?.(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async (values) => {
    if (!organizationId) {
      return;
    }

    setLoading(true);
    try {
      const response = await settingsService.updateOrganizationSettings(organizationId, values);
      setSettingsData((prev) => ({ ...(prev || {}), ...(response.data || {}) }));
      message.success('Organization settings updated successfully');
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to update organization settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout style={{ padding: '20px' }}>
      <Space direction="vertical" size={20} style={{ width: '100%' }}>
        <div>
          <Typography.Title level={2} style={{ marginBottom: 4 }}>Settings</Typography.Title>
          <Typography.Text type="secondary">
            Configure organization-wide fiscal year, payroll template, and leave encashment rules.
          </Typography.Text>
        </div>

        {!organizationId && (
          <Alert
            type="warning"
            message="Organization context is not available yet. Please log in again if this persists."
          />
        )}

        {isManager(user) && (
          <Alert
            type="info"
            message="Manager access is read-only for organization settings."
          />
        )}

        {isEmployee(user) && (
          <Alert
            type="warning"
            message="Employee role does not have access to organization settings."
          />
        )}

        <Spin spinning={loading}>
          <Card title={settingsData?.name ? `${settingsData.name} Settings` : 'Organization Settings'}>
            <Space direction="vertical" size={16} style={{ width: '100%', marginBottom: 16 }}>
              <Typography.Text strong>Organization Logo</Typography.Text>
              {settingsData?.logo ? (
                <img
                  src={settingsData.logo}
                  alt="Organization logo"
                  style={{ width: 120, height: 120, objectFit: 'contain', border: '1px solid #f0f0f0', borderRadius: 8, padding: 8 }}
                />
              ) : (
                <Typography.Text type="secondary">No logo uploaded</Typography.Text>
              )}
              {!readOnly && (
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    if (!file.type.startsWith('image/')) {
                      message.error('Only image files are allowed');
                      return Upload.LIST_IGNORE;
                    }
                    return true;
                  }}
                  customRequest={handleLogoUpload}
                >
                  <Button icon={<UploadOutlined />}>Upload Organization Logo</Button>
                </Upload>
              )}
            </Space>
            <Form
              form={settingsForm}
              layout="vertical"
              onFinish={handleSaveSettings}
              disabled={readOnly}
            >
              <Form.Item
                label="Select Fiscal Year"
                name="selectedFiscalYearId"
                rules={[{ required: true, message: 'Please select a fiscal year' }]}
              >
                <Select
                  placeholder="Choose fiscal year"
                  options={fiscalYears.map((fiscalYear) => ({
                    label: `${fiscalYear.year} (${new Date(fiscalYear.startDate).toLocaleDateString()} - ${new Date(fiscalYear.endDate).toLocaleDateString()})`,
                    value: fiscalYear.id
                  }))}
                />
              </Form.Item>

              <Form.Item
                label="Select Payroll Template"
                name="selectedPayrollTemplateId"
                rules={[{ required: true, message: 'Please select a payroll template' }]}
              >
                <Select
                  placeholder="Choose payroll template"
                  options={payrollTemplates.map((template) => ({
                    label: template.name,
                    value: template.id
                  }))}
                />
              </Form.Item>

              <Form.Item
                label="No of Leaves Carry Forward"
                name="leaveCarryForwardLimit"
                rules={[{ required: true, message: 'Please enter carry forward leaves' }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item label="Is Leave Encashment Available At Every FY" name="leaveEncashmentAtFyEnabled" valuePropName="checked">
                <Switch />
              </Form.Item>

              <Form.Item shouldUpdate noStyle>
                {({ getFieldValue }) => (
                  <Form.Item
                    label="How Many Leaves Can Be Encashed At FY"
                    name="leaveEncashmentAtFyDays"
                    rules={getFieldValue('leaveEncashmentAtFyEnabled') ? [{ required: true, message: 'Please enter FY encashment leaves' }] : []}
                  >
                    <InputNumber min={0} style={{ width: '100%' }} disabled={!getFieldValue('leaveEncashmentAtFyEnabled')} />
                  </Form.Item>
                )}
              </Form.Item>

              <Form.Item label="Is Leave Encashment Available On Exit" name="leaveEncashmentOnExitEnabled" valuePropName="checked">
                <Switch />
              </Form.Item>

              <Form.Item shouldUpdate noStyle>
                {({ getFieldValue }) => (
                  <Form.Item
                    label="Max Leaves Encash On Exit"
                    name="leaveEncashmentOnExitMaxDays"
                    rules={getFieldValue('leaveEncashmentOnExitEnabled') ? [{ required: true, message: 'Please enter max exit encashment leaves' }] : []}
                  >
                    <InputNumber min={0} style={{ width: '100%' }} disabled={!getFieldValue('leaveEncashmentOnExitEnabled')} />
                  </Form.Item>
                )}
              </Form.Item>

              <Space>
                <Button type="primary" htmlType="submit" disabled={readOnly || !organizationId}>
                  Save Settings
                </Button>
              </Space>
            </Form>
          </Card>
        </Spin>
      </Space>
    </Layout>
  );
}

export default Settings;
